import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import {
  listUsers, createUser, updateUser, deleteUser, changeUserPassword,
  type CreateUserInput, type UpdateUserInput,
} from '@/api/users';
import {
  ADMIN_SECTIONS, ADMIN_ADDON_ROLES, APPROVAL_KINDS, BDM_SECTIONS, TRAFFIC_SECTIONS,
  type Admin, type AdminAddonRole, type AdminSection, type ApprovalKind,
} from '@/api/types';
import { useAuthStore } from '@/store/auth';
import { getApps } from '@/api/apps';
import { getCampaigns } from '@/api/tracker-campaigns';
import { getTrackerPwas } from '@/api/tracker-pwas';

const SECTION_LABELS: Record<AdminSection, string> = {
  dashboard: 'Dashboard',
  apps: 'Apps',
  'apps-setup': 'Apps Setup',
  campaigns: 'Campaigns',
  ads: 'Ads',
  pwa: 'PWA',
  funnel: 'Funnel',
  generate: 'Generate',
  referrals: 'Referrals',
  devices: 'Devices',
  offers: 'Offers',
  'offers-performance': 'Offer Performance',
  'funnel-audits': 'Funnel Audits',
  finance: 'Сверка с реклами',
  'public-api': 'Public API',
  browser: 'Live browser',
  perf: 'Performance (app × geo)',
  radar: 'iGaming Radar',
  'aso-market': 'ASO.Market',
  notifications: 'Notifications',
  settings: 'Settings',
  tracker: 'Tracker',
  domains: 'Domains',
  firebase: 'Firebase',
  play: 'Play Console',
  users: 'Users',
  audit: 'Audit Log',
  checklist: 'Checklist',
  credentials: 'Credentials',
  facebook: 'Facebook',
  push: 'Push',
  'push-test': 'Push Test',
  email: 'Email',
  qa: 'QA',
  wiki: 'Wiki',
  'wiki-edit': 'Wiki: правка',
  'wiki-share': 'Wiki: публичные ссылки',
};

// The two wiki sub-grants are meaningless without the base `wiki` one — the
// API denies every /admin/wiki* path without it. Unticking Wiki therefore
// clears both, so the checkboxes cannot describe access the server won't give.
const WIKI_SUBGRANTS: AdminSection[] = ['wiki-edit', 'wiki-share'];

export function normalizeWikiGrants(perms: AdminSection[]): AdminSection[] {
  if (perms.includes('wiki')) return perms;
  return perms.filter((p) => !WIKI_SUBGRANTS.includes(p));
}

// `finance`, `public-api` and `browser` are deliberately absent: they are
// granted through the addon checkboxes, which work for every account type. Leaving them here
// too would mean two controls for one grant, and only one of them visible on a
// BDM.
const ASSIGNABLE_SECTIONS: AdminSection[] = ADMIN_SECTIONS.filter(
  (s) => s !== 'users' && s !== 'finance' && s !== 'public-api' && s !== 'browser',
);

type Mode = 'create' | 'edit' | 'delete' | 'password' | null;
// Base roles only. Developer and QA are addons — see FormState.addons.
type AccountType = 'user' | 'aso' | 'aso_assistant' | 'traffic' | 'bdm' | 'super_admin';

const ADDON_LABELS: Record<AdminAddonRole, string> = {
  developer: 'Developer',
  qa: 'QA',
  finance: 'Сверка с реклами',
  api: 'Public API',
  browser: 'Live browser',
};

const ADDON_HINTS: Record<AdminAddonRole, string> = {
  developer: 'Adds Apps Setup, Firebase, Play Console and Push Test, and the right to create and configure apps.',
  qa: 'Adds product flows, offers, email, analytics, diagnostics, audit history and the QA test-device tools.',
  // An addon rather than a section checkbox: BDM and traffic take a fixed
  // preset, so this is the only way to hand the tab to one person and not to
  // their whole role.
  finance: 'Adds the monthly reconciliation tab: every advertiser\'s deposits, what they approved, and the inbound wallet log. Not narrowed by campaign access.',
  // The outside REST API. Keys are personal (issued and managed by their
  // owner), and what a key makes is attributed to the owner, so the tracker
  // shows it even for a campaign-scoped account.
  api: 'Adds the Public API page: issue own zw_ keys for the outside REST API, see the PWAs provisioned with them, the docs. Also opens Tracker (the campaigns and PWAs the keys create) and Wiki.',
  // Everything but what loses a signed-in session for good (deleting,
  // unbinding the ShardX profile).
  browser: 'Adds the Browser Profiles page: add and edit profiles and workspaces, open a profile or a Play publishing account (its Play Console) on the VPS and drive it from the admin, queue a session check. Deleting profiles and workspaces and the Firebase project limit stay with super admins.',
};

const APPROVAL_LABELS: Record<ApprovalKind, string> = {
  delete: 'Deletions',
  credential_password: 'Passwords',
  credential_totp: '2FA codes',
  facebook_secret: 'Facebook secrets',
  pr_merge: 'PR merge',
  publisher_payment: 'Publisher payments',
  keystore_change: 'Keystore changes',
  asomarket_order: 'ASO.Market orders',
};

const APPROVAL_HINTS: Record<ApprovalKind, string> = {
  delete: 'Approve or reject deletion requests of live objects.',
  credential_password: 'Approve or reject requests to see a partner password in Credentials.',
  credential_totp: 'Approve or reject requests for a partner 2FA code.',
  facebook_secret: 'Approve or reject requests to see a Facebook account\'s secrets.',
  pr_merge: 'Approve and merge GitHub pull requests from their card.',
  publisher_payment: 'Approve a publisher app for payment from its listing card.',
  keystore_change: 'Approve or reject requests to regenerate or upload over an app\'s signing keystore.',
  asomarket_order: 'Send or skip an ASO.Market auto order that costs more than the order cap.',
};

interface FormState {
  email: string;
  name: string;
  password: string;
  account_type: AccountType;
  addons: AdminAddonRole[];
  approvals: ApprovalKind[];
  permissions: AdminSection[];
  read_only: boolean;
  is_active: boolean;
  allowed_apps: string[];
  manual_tracker_campaigns: string[];
  manual_tracker_pwas: string[];
  app_access_mode: 'legacy_all' | 'assigned';
  assistant_of_admin_id: string;
}

const emptyForm: FormState = {
  email: '',
  name: '',
  password: '',
  account_type: 'aso',
  addons: [],
  approvals: [],
  permissions: [],
  read_only: false,
  is_active: true,
  allowed_apps: [],
  manual_tracker_campaigns: [],
  manual_tracker_pwas: [],
  app_access_mode: 'assigned',
  assistant_of_admin_id: '',
};

export function Users() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.admin);

  const [mode, setMode] = useState<Mode>(null);
  const [target, setTarget] = useState<Admin | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('');

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => listUsers().then((r) => r.users),
  });

  const appsQuery = useQuery({ queryKey: ['apps-min'], queryFn: () => getApps(), enabled: mode === 'create' || mode === 'edit' });
  const campaignsQuery = useQuery({
    queryKey: ['tracker-campaigns'],
    queryFn: getCampaigns,
    enabled: (mode === 'create' || mode === 'edit') && (form.account_type === 'aso' || form.account_type === 'traffic'),
  });
  const pwasQuery = useQuery({
    queryKey: ['tracker-pwas'],
    queryFn: getTrackerPwas,
    enabled: (mode === 'create' || mode === 'edit') && form.account_type === 'traffic',
  });

  const createMut = useMutation({
    mutationFn: (input: CreateUserInput) => createUser(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      closeModal();
    },
    onError: (e: any) => setError(e?.message || 'Failed to create user'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) => updateUser(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      closeModal();
    },
    onError: (e: any) => setError(e?.message || 'Failed to update user'),
  });

  const passwordMut = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => changeUserPassword(id, password),
    onSuccess: () => closeModal(),
    onError: (e: any) => setError(e?.message || 'Failed to change password'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      closeModal();
    },
    onError: (e: any) => setError(e?.message || 'Failed to delete user'),
  });

  function openCreate() {
    setForm(emptyForm);
    setError('');
    setMode('create');
    setTarget(null);
  }

  function openEdit(u: Admin) {
    setTarget(u);
    setForm({
      email: u.email,
      name: u.name ?? '',
      password: '',
      account_type: u.role === 'super_admin'
        ? 'super_admin'
        : u.user_type === 'bdm' ? 'bdm' : u.user_type === 'traffic' ? 'traffic'
          : u.assistant_of_admin_id ? 'aso_assistant' : u.user_type === 'aso' ? 'aso' : 'user',
      addons: [...(u.addons ?? [])],
      approvals: u.role === 'super_admin' ? [] : [...(u.approvals ?? [])],
      permissions: u.permissions,
      read_only: u.read_only,
      is_active: u.is_active,
      allowed_apps: [...u.allowed_apps],
      manual_tracker_campaigns: [...(u.manual_tracker_campaigns ?? [])],
      manual_tracker_pwas: [...(u.allowed_tracker_pwas ?? [])],
      app_access_mode: u.app_access_mode,
      assistant_of_admin_id: u.assistant_of_admin_id ?? '',
    });
    setError('');
    setMode('edit');
  }

  function openDelete(u: Admin) {
    setTarget(u);
    setError('');
    setMode('delete');
  }

  function openPassword(u: Admin) {
    setTarget(u);
    setPwd('');
    setError('');
    setMode('password');
  }

  function closeModal() {
    setMode(null);
    setTarget(null);
    setError('');
    setPwd('');
  }

  useEffect(() => {
    if (form.account_type === 'super_admin' && form.permissions.length > 0) {
      setForm((f) => ({ ...f, permissions: [] }));
    } else if (form.account_type === 'bdm') {
      setForm((f) => ({ ...f, permissions: [...BDM_SECTIONS] }));
    } else if (form.account_type === 'traffic') {
      setForm((f) => ({ ...f, permissions: [...TRAFFIC_SECTIONS] }));
    }
  }, [form.account_type]);

  // Turning QA on pre-ticks read_only, matching what a plain tester used to get
  // when QA was a role that forced it. It stays clearable — read_only is now a
  // manual flag, so a QA+developer account can write.
  function toggleAddon(addon: AdminAddonRole) {
    setForm((f) => {
      const on = f.addons.includes(addon);
      const addons = on ? f.addons.filter((a) => a !== addon) : [...f.addons, addon];
      return { ...f, addons, read_only: !on && addon === 'qa' ? true : f.read_only };
    });
  }

  function toggleApproval(kind: ApprovalKind) {
    setForm((f) => ({
      ...f,
      approvals: f.approvals.includes(kind) ? f.approvals.filter((k) => k !== kind) : [...f.approvals, kind],
    }));
  }

  function togglePermission(s: AdminSection) {
    setForm((f) => {
      const next = f.permissions.includes(s)
        ? f.permissions.filter((p) => p !== s)
        : [...f.permissions, s];
      // Ticking a wiki sub-grant implies the base grant; unticking the base
      // grant drops both sub-grants.
      const withBase = WIKI_SUBGRANTS.includes(s) && next.includes(s) && !next.includes('wiki')
        ? [...next, 'wiki' as AdminSection]
        : next;
      return { ...f, permissions: normalizeWikiGrants(withBase) };
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (form.account_type === 'aso_assistant' && !form.assistant_of_admin_id) {
      setError('Select a primary ASO specialist');
      return;
    }
    if (mode === 'create') {
      if (!form.email || !form.password) {
        setError('Email and password are required');
        return;
      }
      if (form.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      createMut.mutate({
        email: form.email,
        password: form.password,
        name: form.name || null,
        role: form.account_type === 'super_admin' ? 'super_admin' : 'user',
        user_type: form.account_type === 'bdm' ? 'bdm' : form.account_type === 'traffic' ? 'traffic' : (form.account_type === 'aso' || form.account_type === 'aso_assistant') ? 'aso' : 'general',
        addons: form.account_type === 'super_admin' ? [] : form.addons,
        approvals: form.account_type === 'super_admin' ? [] : form.approvals,
        assistant_of_admin_id: form.account_type === 'aso_assistant' ? form.assistant_of_admin_id : null,
        app_access_mode: form.account_type === 'traffic' ? form.app_access_mode : undefined,
        permissions: form.account_type === 'super_admin'
          ? []
          : form.account_type === 'bdm' ? [...BDM_SECTIONS] : form.account_type === 'traffic' ? [...TRAFFIC_SECTIONS] : form.permissions,
        read_only: form.read_only,
        is_active: form.is_active,
        allowed_apps: (form.account_type === 'aso' || (form.account_type === 'traffic' && form.app_access_mode === 'assigned')) ? form.allowed_apps : [],
        allowed_tracker_campaigns: (form.account_type === 'aso' || form.account_type === 'traffic') ? form.manual_tracker_campaigns : [],
        allowed_tracker_pwas: form.account_type === 'traffic' ? form.manual_tracker_pwas : [],
      });
    } else if (mode === 'edit' && target) {
      updateMut.mutate({
        id: target.id,
        input: {
          email: form.email !== target.email ? form.email : undefined,
          name: form.name || null,
          role: form.account_type === 'super_admin' ? 'super_admin' : 'user',
          user_type: form.account_type === 'bdm' ? 'bdm' : form.account_type === 'traffic' ? 'traffic' : (form.account_type === 'aso' || form.account_type === 'aso_assistant') ? 'aso' : 'general',
        addons: form.account_type === 'super_admin' ? [] : form.addons,
        approvals: form.account_type === 'super_admin' ? [] : form.approvals,
          assistant_of_admin_id: form.account_type === 'aso_assistant' ? form.assistant_of_admin_id : null,
          app_access_mode: form.account_type === 'traffic' ? form.app_access_mode : undefined,
          permissions: form.account_type === 'super_admin'
            ? []
            : form.account_type === 'bdm' ? [...BDM_SECTIONS] : form.account_type === 'traffic' ? [...TRAFFIC_SECTIONS] : form.permissions,
          read_only: form.read_only,
          is_active: form.is_active,
          allowed_apps: (form.account_type === 'aso' || (form.account_type === 'traffic' && form.app_access_mode === 'assigned')) ? form.allowed_apps : [],
          allowed_tracker_campaigns: (form.account_type === 'aso' || form.account_type === 'traffic') ? form.manual_tracker_campaigns : [],
          allowed_tracker_pwas: form.account_type === 'traffic' ? form.manual_tracker_pwas : [],
        },
      });
    }
  }

  function handlePassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!target) return;
    if (!pwd || pwd.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    passwordMut.mutate({ id: target.id, password: pwd });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-content">Users</h1>
        <Button onClick={openCreate}>+ New user</Button>
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        {usersQuery.isLoading ? (
          <div className="p-6 text-sm text-content-subtle">Loading…</div>
        ) : usersQuery.error ? (
          <div className="p-6 text-sm text-danger">{(usersQuery.error as Error).message}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-left text-xs uppercase tracking-wide text-content-subtle">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Account type</th>
                <th className="px-4 py-3">Sections</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(usersQuery.data || []).map((u) => (
                <tr key={u.id} className="hover:bg-surface-muted">
                  <td className="px-4 py-3 text-content">{u.email}{u.id === me?.id && <span className="ml-2 text-xs text-content-subtle">(you)</span>}</td>
                  <td className="px-4 py-3 text-content-muted">{u.name || '—'}</td>
                  <td className="px-4 py-3">
                    {u.role === 'super_admin' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">Super admin</span>
                    ) : u.user_type === 'bdm' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-fuchsia-100 text-fuchsia-800">BDM</span>
                    ) : u.user_type === 'traffic' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-info-soft text-info">Traffic manager</span>
                    ) : u.assistant_of_admin_id ? (
                      <div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-soft text-warning">ASO assistant</span>
                        <div className="mt-1 text-xs text-content-subtle">for {u.assistant_of_name || u.assistant_of_email || 'ASO specialist'}</div>
                      </div>
                    ) : u.user_type === 'aso' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success-soft text-success">ASO specialist</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-muted text-content-muted">User</span>
                    )}
                    {u.role !== 'super_admin' && (u.approvals ?? []).length > 0 && (
                      <div className="mt-1 text-xs text-content-subtle">
                        Approves in TG: {(u.approvals ?? []).map((k) => APPROVAL_LABELS[k]).join(', ')}
                      </div>
                    )}
                    {u.telegram_linked && (
                      <div className="mt-1 text-xs text-content-subtle">Telegram linked</div>
                    )}
                    {(u.addons ?? []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(u.addons ?? []).map((addon) => (
                          <span
                            key={addon}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              addon === 'developer' ? 'bg-primary/10 text-primary' : 'bg-cyan-100 text-cyan-800'
                            }`}
                          >
                            +{ADDON_LABELS[addon]}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-content-muted">
                    {u.role === 'super_admin'
                      ? <span className="text-content-subtle">All</span>
                      : u.permissions.length === 0
                        ? <span className="text-content-subtle">None</span>
                        : <span className="text-xs">{u.permissions.map((p) => SECTION_LABELS[p]).join(', ')}</span>}
                    {u.role === 'user' && (u.user_type === 'aso' || u.user_type === 'traffic') && (
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs text-content-subtle">Apps: {u.app_access_mode === 'legacy_all' ? 'all' : u.allowed_apps.length}</span>
                        <span className="text-xs text-content-subtle">Tracker: {u.allowed_tracker_campaigns.length} ({u.manual_tracker_campaigns?.length ?? 0} manual)</span>
                      </div>
                    )}
                    {u.role === 'user' && u.user_type === 'bdm' && (
                      <div className="mt-1 text-xs text-content-subtle">Apps: all · Tracker campaigns: all</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {!u.is_active && <span className="px-2 py-0.5 rounded text-xs bg-danger-soft text-danger">Disabled</span>}
                      {u.read_only && u.role !== 'super_admin' && <span className="px-2 py-0.5 rounded text-xs bg-warning-soft text-warning">Read-only</span>}
                      {u.is_active && (!u.read_only || u.role === 'super_admin') && <span className="px-2 py-0.5 rounded text-xs bg-success-soft text-success">Active</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openEdit(u)} className="text-sm text-primary hover:underline">Edit</button>
                      <button onClick={() => openPassword(u)} className="text-sm text-content-muted hover:underline">Password</button>
                      {u.id !== me?.id && (
                        <button onClick={() => openDelete(u)} className="text-sm text-danger hover:underline">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(usersQuery.data || []).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-content-subtle">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal isOpen={mode === 'create' || mode === 'edit'} onClose={closeModal} title={mode === 'create' ? 'New user' : 'Edit user'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                minLength={8}
                required
              />
              <p className="mt-1 text-xs text-content-subtle">At least 8 characters.</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Account type</label>
            <select
              value={form.account_type}
              onChange={(e) => setForm((f) => ({ ...f, account_type: e.target.value as AccountType }))}
              className="w-full px-3 py-2 border border-border-strong rounded-md text-sm bg-surface"
              disabled={mode === 'edit' && target?.id === me?.id}
            >
              <option value="user">User (legacy unrestricted apps)</option>
              <option value="aso">ASO specialist (isolated by apps)</option>
              <option value="aso_assistant">ASO assistant (mirrors a specialist)</option>
              <option value="traffic">Traffic manager (own + assigned campaigns)</option>
              <option value="bdm">BDM (global business access)</option>
              <option value="super_admin">Super admin (full access)</option>
            </select>
            {form.account_type === 'aso' && (
              <p className="mt-1 text-xs text-content-subtle">Sees only assigned apps and everything linked to them.</p>
            )}
            {form.account_type === 'aso_assistant' && (
              <p className="mt-1 text-xs text-content-subtle">Inherits sections, apps, tracker campaigns and notification channels from the selected ASO specialist.</p>
            )}
            {form.account_type === 'user' && (
              <p className="mt-1 text-xs text-warning">Legacy user type can see all apps. Use ASO specialist for isolation.</p>
            )}
            {form.account_type === 'traffic' && (
              <p className="mt-1 text-xs text-content-subtle">Creates tracker campaigns and PWAs; sees assigned campaigns plus campaigns created personally.</p>
            )}
            {form.account_type === 'bdm' && (
              <p className="mt-1 text-xs text-content-subtle">Manages offers and their campaign assignments; sees all apps, campaigns, Offer Performance, Firebase, Credentials, clicks and conversions.</p>
            )}
            {mode === 'edit' && target?.id === me?.id && (
              <p className="mt-1 text-xs text-content-subtle">You cannot change your own role.</p>
            )}
          </div>

          {form.account_type !== 'super_admin' && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">Additional roles</label>
              <div className="space-y-2 rounded-md border border-border bg-surface-muted p-3">
                {ADMIN_ADDON_ROLES.map((addon) => (
                  <label key={addon} className="flex gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.addons.includes(addon)}
                      onChange={() => toggleAddon(addon)}
                      className="mt-0.5 rounded border-border-strong text-primary focus:ring-primary"
                    />
                    <span>
                      <span className="text-content">{ADDON_LABELS[addon]}</span>
                      <span className="block text-xs text-content-subtle">{ADDON_HINTS[addon]}</span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-content-subtle">
                Additional roles only add sections and capabilities on top of the account type. They never change which apps the account can see.
              </p>
            </div>
          )}

          {form.account_type !== 'super_admin' && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">Approvals in Telegram</label>
              <div className="space-y-2 rounded-md border border-border bg-surface-muted p-3">
                {APPROVAL_KINDS.map((kind) => (
                  <label key={kind} className="flex gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.approvals.includes(kind)}
                      onChange={() => toggleApproval(kind)}
                      className="mt-0.5 rounded border-border-strong text-primary focus:ring-primary"
                    />
                    <span>
                      <span className="text-content">{APPROVAL_LABELS[kind]}</span>
                      <span className="block text-xs text-content-subtle">{APPROVAL_HINTS[kind]}</span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-content-subtle">
                Which card buttons this person may press in the Telegram chats (and on the Requests page). They link their Telegram on the Requests page. Nobody approves their own request. Super admins approve everything.
              </p>
            </div>
          )}

          {form.account_type === 'aso_assistant' && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Primary ASO specialist</label>
              <select
                value={form.assistant_of_admin_id}
                onChange={(e) => setForm((f) => ({ ...f, assistant_of_admin_id: e.target.value }))}
                className="w-full px-3 py-2 border border-border-strong rounded-md text-sm bg-surface"
                required
              >
                <option value="">Select specialist…</option>
                {(usersQuery.data ?? [])
                  .filter((u) => u.id !== target?.id && u.role !== 'super_admin' && u.user_type === 'aso' && !u.assistant_of_admin_id && u.is_active)
                  .map((u) => <option key={u.id} value={u.id}>{u.name ? `${u.name} — ` : ''}{u.email}</option>)}
              </select>
            </div>
          )}

          {(form.account_type === 'user' || form.account_type === 'aso') && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">Section access</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border border-border rounded-md bg-surface-muted">
                {ASSIGNABLE_SECTIONS.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(s)}
                      onChange={() => togglePermission(s)}
                      className="rounded border-border-strong text-primary focus:ring-primary"
                    />
                    {SECTION_LABELS[s]}
                  </label>
                ))}
              </div>
              <div className="mt-2 flex gap-3 text-xs">
                <button type="button" onClick={() => setForm((f) => ({ ...f, permissions: [...ASSIGNABLE_SECTIONS] }))} className="text-primary hover:underline">Select all</button>
                <button type="button" onClick={() => setForm((f) => ({ ...f, permissions: [] }))} className="text-content-subtle hover:underline">Clear</button>
              </div>
            </div>
          )}

          {form.account_type === 'traffic' && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">App access mode</label>
              <div className="flex gap-4 rounded-md border border-border bg-surface-muted p-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={form.app_access_mode === 'legacy_all'} onChange={() => setForm((f) => ({ ...f, app_access_mode: 'legacy_all' }))} />
                  All apps (including future)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={form.app_access_mode === 'assigned'} onChange={() => setForm((f) => ({ ...f, app_access_mode: 'assigned' }))} />
                  Selected apps
                </label>
              </div>
            </div>
          )}

          {(form.account_type === 'aso' || (form.account_type === 'traffic' && form.app_access_mode === 'assigned')) && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">App access</label>
              <p className="text-xs text-content-subtle mb-2">
                {form.account_type === 'traffic'
                  ? 'Empty selection means no app access. Campaigns created by this user remain available in their campaign scope.'
                  : 'Empty selection means no app access. Apps created by this specialist are added automatically. Tracker, Firebase, Play and analytics inherit the same scope.'}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 border border-border rounded-md bg-surface-muted max-h-48 overflow-y-auto">
                  {(appsQuery.data ?? []).map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.allowed_apps.includes(a.id)}
                        onChange={() => setForm((f) => ({ ...f, allowed_apps: f.allowed_apps.includes(a.id) ? f.allowed_apps.filter((x) => x !== a.id) : [...f.allowed_apps, a.id] }))}
                        className="rounded border-border-strong text-primary focus:ring-primary"
                      />
                      {a.name}
                    </label>
                  ))}
              </div>
            </div>
          )}

          {(form.account_type === 'aso' || form.account_type === 'traffic') && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">Manual tracker campaign access</label>
              <p className="text-xs text-content-subtle mb-2">
                {form.account_type === 'traffic'
                  ? 'Selected campaigns are combined with every campaign this user creates.'
                  : 'Campaigns are included automatically when their App is assigned or their exact name matches an assigned app bundle. Select only extra campaigns here.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-border rounded-md bg-surface-muted max-h-48 overflow-y-auto">
                {(campaignsQuery.data ?? []).map((campaign) => (
                  <label key={campaign.id} className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.manual_tracker_campaigns.includes(campaign.id)}
                      onChange={() => setForm((f) => ({
                        ...f,
                        manual_tracker_campaigns: f.manual_tracker_campaigns.includes(campaign.id)
                          ? f.manual_tracker_campaigns.filter((id) => id !== campaign.id)
                          : [...f.manual_tracker_campaigns, campaign.id],
                      }))}
                      className="rounded border-border-strong text-primary focus:ring-primary"
                    />
                    <span className="truncate" title={`${campaign.name} · ${campaign.label || campaign.slug}`}>
                      {campaign.name} · {campaign.label || campaign.slug}
                    </span>
                  </label>
                ))}
                {!campaignsQuery.isLoading && (campaignsQuery.data ?? []).length === 0 && (
                  <span className="text-sm text-content-subtle">No tracker campaigns.</span>
                )}
              </div>
            </div>
          )}

          {form.account_type === 'traffic' && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">Tracker PWA access</label>
              <p className="text-xs text-content-subtle mb-2">
                PWAs are standalone landings, not tied to an app. Selected PWAs are combined with
                every PWA this user creates and with the PWAs their campaigns already use. Selected
                ones can also be edited by this user.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-border rounded-md bg-surface-muted max-h-48 overflow-y-auto">
                {(pwasQuery.data ?? []).map((pwa) => (
                  <label key={pwa.id} className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.manual_tracker_pwas.includes(pwa.id)}
                      onChange={() => setForm((f) => ({
                        ...f,
                        manual_tracker_pwas: f.manual_tracker_pwas.includes(pwa.id)
                          ? f.manual_tracker_pwas.filter((id) => id !== pwa.id)
                          : [...f.manual_tracker_pwas, pwa.id],
                      }))}
                      className="rounded border-border-strong text-primary focus:ring-primary"
                    />
                    <span className="truncate" title={`${pwa.name} · ${pwa.app_name || pwa.category}`}>
                      {pwa.name} · {pwa.app_name || pwa.category}
                    </span>
                  </label>
                ))}
                {!pwasQuery.isLoading && (pwasQuery.data ?? []).length === 0 && (
                  <span className="text-sm text-content-subtle">No tracker PWAs.</span>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.read_only}
                onChange={(e) => setForm((f) => ({ ...f, read_only: e.target.checked }))}
                disabled={mode === 'edit' && target?.id === me?.id}
                className="rounded border-border-strong text-primary focus:ring-primary"
              />
              <span>Read-only (block all edits / deletes / creations)</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                disabled={mode === 'edit' && target?.id === me?.id}
                className="rounded border-border-strong text-primary focus:ring-primary"
              />
              <span>Active (uncheck to disable login)</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" loading={createMut.isPending || updateMut.isPending}>
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={mode === 'password'} onClose={closeModal} title={`Change password${target ? ` — ${target.email}` : ''}`}>
        <form onSubmit={handlePassword} className="space-y-4">
          {error && <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">New password</label>
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              minLength={8}
              required
            />
            <p className="mt-1 text-xs text-content-subtle">At least 8 characters.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" loading={passwordMut.isPending}>Update password</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={mode === 'delete'} onClose={closeModal} title="Delete user">
        <div className="space-y-4">
          {error && <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{error}</div>}
          <p className="text-sm text-content-muted">
            Delete <span className="font-medium">{target?.email}</span>? This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button
              type="button"
              variant="danger"
              loading={deleteMut.isPending}
              onClick={() => target && deleteMut.mutate(target.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
